import { Component } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { OperationsService, TeamsService } from '@innersight-os/shared/firebase-services';
import { filter, map, Observable, of, switchMap } from 'rxjs';
import { StageType } from '@innersight-os/shared/ui-base'
import { OpStatus } from '@innersight-os/shared/firestore-interfaces';

interface Crumb {
  name: string
  // eslint-able-next-line @typescript-eslint/no-explicit-any
  url?: any[] // any[] is type signature of angular Router.navigate arg
  query?: Record<string, string>
}

type Crumbs$ = Observable<Crumb[]>

// see apps/segue/src/app/operations/operations/operations.component.ts
// function of status and upload method
const STATUS_TO_STAGE: Record<OpStatus, StageType> = {
  created: 'uploading',
  uploaded: 'uploaded',
  iepRequestSubmitted: 'iep',
  iepRequestFailed: 'iep',
  noSuitableVolumes: 'uploading',
  withUploader: 'uploading',
  withSegmenter: 'segmenting',
  withReviewer: 'reviewing',
  published: 'published',
  closed: 'closed',
}

@Component({
    selector: 'is-breadcrumbs',
    template: `
    <ng-container *ngFor="let crumb of breadcrumbs$ | async; first as isFirst; last as isLast">
      <a
        [routerLink]="crumb.url"
        [queryParams]="crumb.query"
        routerLinkActive="active"
        [routerLinkActiveOptions]="{
          paths: 'exact',
          matrixParams: 'exact',
          queryParams: 'subset',
          fragment: 'ignored'
        }"
        [class.first]="isFirst">{{crumb.name}}</a>
      <span *ngIf="!isLast">/</span>
    </ng-container>
  `,
    styles: [
        `
    * {
      user-select: none;
    }
    #palette {
      margin-left: 4px;
      font-size: 1.5em;
      vertical-align: middle;
    }
    a {
      text-decoration: none;
      color: #000000;
      font-size: 0.7em;
      font-weight: 400;
    }
    a.active {
      color: #555555;
      cursor: default;
    }
    span {
      margin: 0 2px;
      color: #999999;
      font-weight: 500;
      font-size: 0.8em;
    }`
    ],
    standalone: false
})
export class BreadcrumbsComponent {
  breadcrumbs$ = this.router.events.pipe(
    filter(e => e instanceof NavigationEnd),
    switchMap((): Crumbs$ => {
      const {firstChild} = this.activatedRoute.root
      const [firstSegment] = firstChild.snapshot.url

      if(!firstSegment) return of([])

      if(firstSegment.path === 'operations') {
        const {operationId} = firstSegment.parameters
        return operationId
          ? this.operations.getOperation(operationId).pipe(
              switchMap(operation => this.teams.getTeam(operation.team).pipe(
                map(team => [
                  {name: 'Operations', url: ['operations']},
                  {name: `${team.name} : ${operation.reference}`},
                ])
              ))
            )
          : of([{name: 'Operations'}])
      }

      if(firstSegment.path === 'segment') {
        // the youngest descendant has access to all the params
        let youngest = firstChild
        while(youngest.firstChild && youngest.firstChild.outlet === 'primary') youngest = youngest.firstChild
        const operationId = youngest.snapshot.paramMap.get('operationId')
        const volumeId = youngest.snapshot.paramMap.get('volumeId')

        return this.operations.getOperation(operationId).pipe(
          switchMap(operation => this.teams.getTeam(operation.team).pipe(
            map(team => [
              {name: 'Operations', url: ['operations']},
              {name: `${team.name} : ${operation.reference}`, url: ['operations', {operationId}], query: {team: `${team.id}`, stage: STATUS_TO_STAGE[operation.status] } },
              {name: `${operation.notes}`},
              {name: volumeId.substring(0, 5)},
            ])
          ))
        )
      }

      if(firstSegment.path === 'register') {
        let youngest = firstChild
        while(youngest.firstChild && youngest.firstChild.outlet === 'primary') youngest = youngest.firstChild
        const operationId = youngest.snapshot.paramMap.get('operationId')

        return this.operations.getOperation(operationId).pipe(
          switchMap(operation => this.teams.getTeam(operation.team).pipe(
            map(team => [
              {name: 'Operations', url: ['operations']},
              {name: `${team.name} : ${operation.reference}`, url: ['operations', {operationId}], query: {team: `${team.id}`, stage: STATUS_TO_STAGE[operation.status] } },
            ])
          ))
        )
      }

      return of([]) // default
    }),
  )

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private operations: OperationsService,
    private teams: TeamsService,
  ) {}
}
